import { AccountInfo, AuthenticationResult } from "@azure/msal-browser";
import { UserRole, USER_ROLES, getRoleFromGuid } from "../config/authConfig";
import type { AppRole } from "../services/apiService";

type AppRoleSource = AppRole[] | string[];

const appRoleKeys = (appRoles: AppRoleSource): string[] =>
  appRoles.map((role) => (typeof role === "string" ? role : role.role_key));

/**
 * Extracts Entra JWT coarse roles from the ID token claims (employee/admin/founder).
 * App RBAC roles (red_team / infra_admin) come from GET /me/roles, not the JWT.
 */
export const getUserRoles = (account: AccountInfo | null): UserRole[] => {
  if (!account?.idTokenClaims) {
    return [];
  }

  const claims = account.idTokenClaims as any;
  const roleGuids = claims.roles || [];
  const validRoles: UserRole[] = [];

  if (Array.isArray(roleGuids)) {
    roleGuids.forEach((roleGuid: string) => {
      const mappedRole = getRoleFromGuid(roleGuid);
      if (mappedRole) {
        validRoles.push(mappedRole);
      }
    });
  } else if (typeof roleGuids === "string") {
    const mappedRole = getRoleFromGuid(roleGuids);
    if (mappedRole) {
      validRoles.push(mappedRole);
    }
  }

  return validRoles;
};

/**
 * Checks if user has a specific Entra JWT role
 */
export const hasRole = (account: AccountInfo | null, role: UserRole): boolean => {
  const userRoles = getUserRoles(account);
  return userRoles.includes(role);
};

export const hasAppRole = (
  appRoles: AppRoleSource,
  role: UserRole | string,
): boolean => {
  return appRoleKeys(appRoles).includes(role);
};

/**
 * Checks if user has employee role
 */
export const isEmployee = (account: AccountInfo | null): boolean => {
  return hasRole(account, USER_ROLES.EMPLOYEE);
};

/**
 * Checks if user has admin role
 */
export const isAdmin = (account: AccountInfo | null): boolean => {
  return hasRole(account, USER_ROLES.ADMIN);
};

/**
 * Checks if user has founder role
 */
export const isFounder = (account: AccountInfo | null): boolean => {
  return hasRole(account, USER_ROLES.FOUNDER);
};

/**
 * Checks if user has red_team app RBAC role
 */
export const isRedTeam = (appRoles: AppRoleSource): boolean => {
  return hasAppRole(appRoles, USER_ROLES.RED_TEAM);
};

/**
 * Checks if user has infra_admin app RBAC role
 */
export const isInfraAdmin = (appRoles: AppRoleSource): boolean => {
  return hasAppRole(appRoles, USER_ROLES.INFRA_ADMIN);
};

/**
 * Red Team UI access: red_team or infra_admin (matches API RequireAppRole).
 */
export const canAccessRedTeam = (appRoles: AppRoleSource): boolean => {
  return isRedTeam(appRoles) || isInfraAdmin(appRoles);
};

/**
 * Checks if user has any valid Entra JWT role (employee, admin, or founder)
 */
export const hasAnyRole = (account: AccountInfo | null): boolean => {
  const userRoles = getUserRoles(account);
  return userRoles.length > 0;
};

/**
 * Gets the ID token from the authentication result
 */
export const getIdToken = (authResult: AuthenticationResult | null): string | null => {
  return authResult?.idToken || null;
};

/**
 * Gets user display name
 */
export const getUserDisplayName = (account: AccountInfo | null): string => {
  if (!account) return 'Unknown User';
  
  return account.name || account.username || 'Unknown User';
};

/**
 * Gets user email
 */
export const getUserEmail = (account: AccountInfo | null): string => {
  if (!account) return '';
  
  return account.username || '';
};
